import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import bookingProtectionGlossaryMessages from 'i18n/bookingProtectionGlossaryMessages'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSpoofed as isSpoofedSelector } from 'selectors/isSpoofed'

type InsuranceUpsellTileProduct = 'booking-protection' | 'travel-insurance'

interface InsuranceUpsellTileCopy {
  title: string
  description: string
}

/**
 * Copy for the post-purchase insurance upsell tiles. At most one of the two renders for a
 * given order, and a spoofed session reaches the agent insurance section from either one,
 * where cancellation cover, XCover and PassportCard are all attachable. Both tiles name that
 * wider choice in spoof; customers keep the copy for the single product on offer.
 */
function useInsuranceUpsellTileCopy(product: InsuranceUpsellTileProduct): InsuranceUpsellTileCopy {
  const intl = useIntl()
  const isSpoofed = useAppSelector(isSpoofedSelector)

  return useMemo(() => {
    if (isSpoofed) {
      return {
        title: intl.formatMessage({
          id: '0EVsPitPZgA1hfOnJEeI',
          defaultMessage: 'Cancellation Protection / Travel Insurance',
        }),
        description: intl.formatMessage({
          id: '7KN0XmRo-rZeeLLwj5Sx',
          defaultMessage: 'Add cancellation cover or full travel insurance to this booking.',
        }),
      }
    }

    if (product === 'booking-protection') {
      return {
        title: intl.formatMessage(bookingProtectionGlossaryMessages.cancellationProtection),
        description: intl.formatMessage({
          id: 'DGka_h6331maQIt0eDls',
          defaultMessage: 'Receive a 100% refund if you need to cancel for unforeseen events.',
        }),
      }
    }

    return {
      title: intl.formatMessage({
        id: '-MW3yHw-nHxdX4qELFvn',
        defaultMessage: 'Travel protection',
      }),
      description: intl.formatMessage({
        id: 'PJu1Vz0xmUmrxfE3kbZc',
        defaultMessage: 'Be covered for medical, cancellations and lost baggage.',
      }),
    }
  }, [intl, isSpoofed, product])
}

export default useInsuranceUpsellTileCopy
